'use client'

import { Progress } from '@/components/ui/progress'
import { BodyText } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import type { CollectionProgress } from '@/lib/collections'

interface CollectionProgressBarProps {
  progress: CollectionProgress | null | undefined
  totalOverride?: number // Optional override for total (e.g., from item_count)
  className?: string
  showProgressBar?: boolean
  /** Show Anki-style study counts (New/Learn/Due) */
  showStudyCounts?: boolean
}

export function CollectionProgressBar({
  progress,
  totalOverride,
  className,
  showProgressBar = true,
  showStudyCounts = true
}: CollectionProgressBarProps) {
  // Use override if provided, otherwise use progress data
  const total = totalOverride ?? progress?.total ?? 0
  const learned = progress?.learned ?? 0

  // Anki-style counts (new_remaining is already capped by limit)
  const newRemaining = progress?.new_remaining ?? 0
  const learnCount = progress?.learn_count ?? 0
  const dueCount = progress?.due_count ?? 0

  // Calculate percentage
  const percentage = total > 0 ? Math.round((learned / total) * 100) : 0

  // Don't render if no data
  if (total === 0 && learned === 0) {
    return null
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      {/* Anki-style study counts */}
      {showStudyCounts && (
        <div className="flex items-center gap-2 text-xs font-medium">
          <span className="text-blue-500 dark:text-blue-400">
            {newRemaining} new
          </span>
          <span className="text-orange-500 dark:text-orange-400">
            {learnCount} learn
          </span>
          <span className="text-green-600 dark:text-green-400">
            {dueCount} due
          </span>
        </div>
      )}

      {/* Progress bar and percentage */}
      <div className="flex items-center justify-between gap-2">
        <BodyText className="text-muted-foreground text-xs font-medium">
          {learned} / {total}
        </BodyText>
        {showProgressBar && (
          <BodyText className="text-muted-foreground mt-0! text-xs">
            {percentage}%
          </BodyText>
        )}
      </div>
      {showProgressBar && (
        <Progress value={percentage} size="md" className="w-full" />
      )}
    </div>
  )
}
